import {Button} from '@chekinapp/ui';
import {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {HighlightedTitle, useStepperStore} from '@chekin/shared';
import {IVDocumentPreview} from '@chekin/shared/guest-form';
import {useDocumentSelection} from 'hooks/iv/useDocumentSelection';
import {GUEST_FORM_NAMES} from '@chekin/core';
import {PageLayout} from 'components/PageLayout';
import {SkipForNow} from 'components/common/SkipForNow';
import {
  DocumentSelectionFields,
  type DocumentSelectionFormTypes,
} from 'components/DocumentSelection';
import {RegistrationStepSubtitle} from 'components/RegistrationProgress';
import {useGuardedNavigate} from 'context/navigationGuard';
import {useIVBackNavigation} from 'hooks/iv/useIVBackNavigation';
import {useIVComputedDetails} from 'hooks';
import {useIVCaptureKicker} from '../../ivCaptureCopy';

function DocumentSelectionView() {
  const {control, watch, handleSubmit, setValue} = useForm<DocumentSelectionFormTypes>();
  const {t} = useTranslation();
  const guardedNavigate = useGuardedNavigate();
  const {getPreviousPath} = useIVBackNavigation();
  const handleBack = () => guardedNavigate(getPreviousPath(), {replace: true});
  const {resetIVProcess} = useStepperStore();
  const {confirm: onConfirm, isLoading} = useDocumentSelection();
  const {biomatch} = useIVComputedDetails();
  const kicker = useIVCaptureKicker(t('guest_registration'));

  const nationality = watch(GUEST_FORM_NAMES.nationality);
  const documentType = watch(GUEST_FORM_NAMES.document_type);

  useEffect(() => {
    resetIVProcess();
  }, [resetIVProcess]);

  return (
    <PageLayout
      kicker={kicker}
      className="iv-document-setup-view"
      fitViewport
      title={<HighlightedTitle i18nKey="choose_document_heading" />}
      intro={t(biomatch ? 'document_screen_intro_selfie' : 'document_screen_intro')}
      backLabel={t('back')}
      onBack={handleBack}
      titleAction={<SkipForNow />}
      mobileTitle={t('guest_registration_title')}
      mobileSubtitle={<RegistrationStepSubtitle />}
      dock={
        <Button
          onClick={handleSubmit(onConfirm)}
          loading={isLoading}
          disabled={!nationality || !documentType}
          className="w-full capitalize lg:w-[280px]"
        >
          {t('continue')}
        </Button>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col pb-6 lg:pb-0">
        <DocumentSelectionFields
          control={control}
          watch={watch}
          setValue={setValue}
          withSelfie={biomatch}
          layout="grid"
        />
        <IVDocumentPreview
          nationality={nationality}
          documentType={documentType}
          withSelfie={biomatch}
          className="mt-3.5"
        />
      </div>
    </PageLayout>
  );
}

export {DocumentSelectionView};
